import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSocket } from '../../store/SocketContext';
import { API_PATH, request } from '../../config/api';
import { Loader2, AlertTriangle, ArrowLeft } from 'lucide-react';
import ResultsView from '../../components/activities/ResultsView';
import Tooltip from '../../components/ui/Tooltip';
import livePulseLogo from '../../assets/live-pulse-logo.png';
import styles from './PresenterView.module.css';

const API = API_PATH;
const POLL_INTERVAL_MS = 2500;
const ACTIVITY_REFRESH_MS = 3000; // refetch activity so settings (e.g. showCorrectAnswer) stay in sync

function loadEvents() {
    return request(`${API}/events`)
        .then((r) => r.ok ? r.json() : Promise.reject(new Error('Failed to load events')))
        .then((data) => (Array.isArray(data) ? data : []));
}

function loadEvent(eventId) {
    return loadEvents().then((list) => list.find((e) => String(e.id) === String(eventId)) || null);
}

function loadActivity(eventId, activityId) {
    return request(`${API}/events/${eventId}/activities`)
        .then((r) => r.ok ? r.json() : Promise.reject(new Error('Failed to load activity')))
        .then((data) => {
            const list = Array.isArray(data) ? data : [];
            return list.find((a) => String(a.id) === String(activityId)) || null;
        });
}

function loadResults(eventId, activityId) {
    return request(`${API}/events/${eventId}/activities/${activityId}/results`)
        .then((r) => {
            if (!r.ok) throw new Error(r.status === 404 ? 'Activity not found' : `Error ${r.status}`);
            return r.json();
        })
        .then((data) => {
            const arr = Array.isArray(data) ? data : [];
            return arr.map((r) => {
                let answer = r.answer;
                if (typeof answer === 'string') {
                    try {
                        answer = JSON.parse(answer);
                    } catch {
                        answer = null;
                    }
                }
                return { ...r, answer };
            });
        });
}

/**
 * PresenterView — Detached / second-screen view for the event's current live poll.
 * Follows active_activity_id: when admin switches to next poll, this view updates automatically.
 * Real-time: new responses via socket; current poll via polling event's active_activity_id.
 */
export default function PresenterView() {
    const { eventId } = useParams();
    const navigate = useNavigate();
    const socket = useSocket();
    const [event, setEvent] = useState(null);
    const [activity, setActivity] = useState(null);
    const [responses, setResponses] = useState([]);
    const [participantsCount, setParticipantsCount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [noActivePoll, setNoActivePoll] = useState(false);
    const currentActivityIdRef = useRef(null);

    const activeActivityId = event?.active_activity_id ?? null;

    // Load event and set active activity
    useEffect(() => {
        if (!eventId) {
            setError('Invalid URL');
            setIsLoading(false);
            return;
        }
        setError(null);
        setNoActivePoll(false);
        setIsLoading(true);
        loadEvent(eventId)
            .then((ev) => {
                setEvent(ev);
                if (!ev) setError('Event not found');
                else if (ev.active_activity_id == null) setNoActivePoll(true);
            })
            .catch((err) => setError(err.message || 'Failed to load'))
            .finally(() => setIsLoading(false));
    }, [eventId]);

    // Poll events to detect when admin switches to next/previous poll
    useEffect(() => {
        if (!eventId) return;
        const interval = setInterval(() => {
            loadEvents()
                .then((list) => {
                    const ev = list.find((e) => String(e.id) === String(eventId));
                    if (ev) setEvent(ev);
                })
                .catch(() => {});
        }, POLL_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [eventId]);

    // When active_activity_id is set or changes, load that activity and its results
    useEffect(() => {
        if (!eventId || activeActivityId == null) {
            if (activity || responses.length) {
                setActivity(null);
                setResponses([]);
            }
            currentActivityIdRef.current = null;
            return;
        }
        setNoActivePoll(false);
        if (currentActivityIdRef.current === String(activeActivityId)) return;
        currentActivityIdRef.current = String(activeActivityId);
        setError(null);
        Promise.all([
            loadActivity(eventId, activeActivityId),
            loadResults(eventId, activeActivityId),
        ])
            .then(([act, res]) => {
                setActivity(act);
                setResponses(res);
                setError(act ? null : 'Activity not found');
            })
            .catch((err) => {
                setError(err.message || 'Failed to load');
                setActivity(null);
                setResponses([]);
            });
    }, [eventId, activeActivityId]);

    // Periodically refetch only to sync "Mark correct answer"; do NOT overwrite results hide/show or voting lock
    useEffect(() => {
        if (!eventId || activeActivityId == null) return;
        const interval = setInterval(() => {
            loadActivity(eventId, activeActivityId)
                .then((act) => {
                    if (!act) return;
                    setActivity((prev) => {
                        if (!prev) return act;
                        const next = { ...act };
                        const prevSettings = prev.content?.settings || {};
                        const newSettings = act.content?.settings || {};
                        next.content = {
                            ...act.content,
                            correctOptions: act.content?.correctOptions ?? prev.content?.correctOptions,
                            settings: {
                                ...newSettings,
                                pollResults: prevSettings.pollResults,
                                isLocked: prevSettings.isLocked,
                                showCorrectAnswer: newSettings.showCorrectAnswer,
                            },
                        };
                        return next;
                    });
                })
                .catch(() => {});
        }, ACTIVITY_REFRESH_MS);
        return () => clearInterval(interval);
    }, [eventId, activeActivityId]);

    // Socket: room count + live responses for current activity
    useEffect(() => {
        if (!socket || !eventId) return;
        socket.emit('admin:join', { eventId });
        const handleRoomUpdate = ({ count: c }) => setParticipantsCount(c ?? 0);
        socket.on('room:update', handleRoomUpdate);
        // A socket drop can land at the exact moment a participant responds; that broadcast is
        // never replayed. The poll-switch above is already covered by the 2.5s events poll, so
        // this only needs to re-sync the response list for whichever activity is live now.
        const handleConnect = () => {
            socket.emit('admin:join', { eventId });
            if (activeActivityId != null) {
                loadResults(eventId, activeActivityId).then(setResponses).catch(() => {});
            }
        };
        socket.on('connect', handleConnect);
        const handler = ({ activityId: aid, responseId, answer, participant, createdAt }) => {
            if (String(aid) !== String(activeActivityId)) return;
            let parsed = answer;
            if (typeof answer === 'string') {
                try {
                    parsed = JSON.parse(answer);
                } catch (e) {
                    console.error('Parse answer:', e);
                }
            }
            const newRow = { id: responseId, answer: parsed, participant, created_at: createdAt, reactions: {} };
            setResponses((prev) => {
                // Match by responseId, not participant name — see JoinPage.jsx's handleNewResponse
                // for why: matching by name would incorrectly merge two different Anonymous
                // participants' responses into one row, even though the DB stores them separately.
                const idx = responseId != null
                    ? prev.findIndex((r) => r.id != null && Number(r.id) === Number(responseId))
                    : -1;
                if (idx > -1) {
                    const next = [...prev];
                    next[idx] = { ...next[idx], ...newRow };
                    return next;
                }
                return [...prev, newRow];
            });
        };
        const handleResponseDeleted = ({ activityId: aid }) => {
            if (String(aid) !== String(activeActivityId)) return;
            loadResults(eventId, activeActivityId)
                .then((parsed) => setResponses(parsed))
                .catch(() => {});
        };
        const handleResponseReaction = ({ activityId: aid, responseId, reactions }) => {
            if (String(aid) !== String(activeActivityId)) return;
            setResponses((prev) => prev.map((r) => (r.id != null && Number(r.id) === Number(responseId) ? { ...r, reactions } : r)));
        };
        const handleResponseUpdated = ({ activityId: aid, responseId, answer: rawAnswer }) => {
            if (String(aid) !== String(activeActivityId)) return;
            const answer = typeof rawAnswer === 'string' ? (() => { try { return JSON.parse(rawAnswer); } catch { return rawAnswer; } })() : rawAnswer;
            setResponses((prev) => prev.map((r) => (r.id != null && Number(r.id) === Number(responseId) ? { ...r, answer } : r)));
        };
        socket.on('response:new', handler);
        socket.on('response:deleted', handleResponseDeleted);
        socket.on('response:reaction', handleResponseReaction);
        socket.on('response:updated', handleResponseUpdated);
        return () => {
            socket.off('response:new', handler);
            socket.off('response:deleted', handleResponseDeleted);
            socket.off('response:reaction', handleResponseReaction);
            socket.off('response:updated', handleResponseUpdated);
            socket.off('room:update', handleRoomUpdate);
            socket.off('connect', handleConnect);
        };
    }, [socket, eventId, activeActivityId]);

    const content = useMemo(() => {
        const raw = activity?.content;
        if (raw == null) return null;
        if (typeof raw === 'object') return raw;
        if (typeof raw === 'string') {
            try {
                return JSON.parse(raw);
            } catch {
                return null;
            }
        }
        return null;
    }, [activity?.content]);

    const type = activity?.type;
    const imageUrl = content?.settings?.imageUrl ?? content?.settings?.image_url;
    const hasImage = imageUrl && String(imageUrl).trim();
    const question = content?.question;
    const description = content?.settings?.descText?.trim();
    const baseUrl = window.location.origin;
    const joinUrl = event?.code ? `${baseUrl}/join/${event.code}` : '';
    const qrImageUrl = joinUrl
        ? `https://api.qrserver.com/v1/create-qr-code/?size=160x160&margin=8&color=0c0c10&bgcolor=ffffff&data=${encodeURIComponent(joinUrl)}`
        : '';

    if (isLoading && !event) {
        return (
            <div className={styles.wrap}>
                <div className={styles.loading}>
                    <Loader2 size={32} className={styles.spinner} />
                    <span>Loading…</span>
                </div>
            </div>
        );
    }

    if (error && !event) {
        return (
            <div className={styles.wrap}>
                <div className={styles.error}>
                    <AlertTriangle size={24} />
                    <p>{error}</p>
                    <button type="button" className={styles.backBtn} onClick={() => navigate(-1)}>
                        <ArrowLeft size={16} /> Go back
                    </button>
                </div>
            </div>
        );
    }

    if (noActivePoll || (event && !activeActivityId)) {
        const joinUrl = `${window.location.origin}`;
        const joinUrlNoPoll = event?.code ? `${window.location.origin}/join/${event.code}` : '';
        const qrImageUrlNoPoll = joinUrlNoPoll
            ? `https://api.qrserver.com/v1/create-qr-code/?size=160x160&margin=8&color=0c0c10&bgcolor=ffffff&data=${encodeURIComponent(joinUrlNoPoll)}`
            : '';
        return (
            <div className={styles.wrap}>
                <header className={styles.header}>
                    <div className={styles.headerMeta}>
                        <span className={styles.eventName}>{event?.name || 'Event'}</span>
                    </div>
                    <Tooltip title="Close" placement="bottom">
                        <button type="button" className={styles.backBtn} onClick={() => window.close()}>
                            <ArrowLeft size={16} /> Close
                        </button>
                    </Tooltip>
                </header>
                <main className={styles.main}>
                    {event?.code && (
                        <aside className={styles.leftPanel}>
                            <div className={styles.leftPanelBrand}>
                                <img src={livePulseLogo} alt="LivePulse" className={styles.brandIcon} />
                            </div>
                            <div className={styles.joinCodeBlock}>
                                <span className={styles.joinLabel}>Event code</span>
                                <span className={styles.joinCode}>{event.code}</span>
                            </div>
                            {qrImageUrlNoPoll && (
                                <div className={styles.joinQrBlock}>
                                    <span className={styles.joinLabel}>Scan to join</span>
                                    <img src={qrImageUrlNoPoll} alt="QR code to join" className={styles.joinQrImage} />
                                </div>
                            )}
                            <div className={styles.joinInstructions}>
                                <span className={styles.joinLabel}>How to join</span>
                                <ol className={styles.joinSteps}>
                                    <li>Scan the QR code with your phone, or</li>
                                    <li>Open the <a href={baseUrl} target="_blank" rel="noopener noreferrer" className={styles.joinLink}> {baseUrl}</a> link in your browser and enter the event code above</li>
                                </ol>
                            </div>
                        </aside>
                    )}
                    <div className={styles.noActivePoll}>
                        <p>No active poll right now.</p>
                        <p className={styles.noActivePollHint}>When the presenter starts a poll, it will appear here automatically.</p>
                    </div>
                </main>
            </div>
        );
    }

    if (!activity && activeActivityId) {
        return (
            <div className={styles.wrap}>
                <div className={styles.loading}>
                    <Loader2 size={32} className={styles.spinner} />
                    <span>Loading poll…</span>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.wrap}>
            <header className={styles.header}>
                <div className={styles.headerMeta}>
                    <span className={styles.eventName}>{event?.name}</span>
                    <span className={styles.stats}>
                        <span className={styles.stat}>{responses.length} response{responses.length !== 1 ? 's' : ''}</span>
                        <span className={styles.statDot} aria-hidden>·</span>
                        <span className={styles.stat}>{participantsCount} online</span>
                    </span>
                </div>
                <Tooltip title="Close this window" placement="bottom">
                    <button
                        type="button"
                        className={styles.backBtn}
                        onClick={() => window.close()}
                    >
                        <ArrowLeft size={16} /> Close
                    </button>
                </Tooltip>
            </header>

            <main className={styles.main}>
                {event?.code && (
                    <aside className={styles.leftPanel}>
                        <div className={styles.leftPanelBrand}>
                            <img src={livePulseLogo} alt="LivePulse" className={styles.brandIcon} />
                        </div>
                        <div className={styles.joinCodeBlock}>
                            <span className={styles.joinLabel}>Event code</span>
                            <span className={styles.joinCode}>{event.code}</span>
                        </div>
                        {qrImageUrl && (
                            <div className={styles.joinQrBlock}>
                                <span className={styles.joinLabel}>Scan to join</span>
                                <img src={qrImageUrl} alt="QR code to join" className={styles.joinQrImage} />
                            </div>
                        )}
                        <div className={styles.joinInstructions}>
                            <span className={styles.joinLabel}>How to join</span>
                            <ol className={styles.joinSteps}>
                                <li>Scan the QR code with your phone, or</li>
                                <li>Open the <a href={baseUrl} target="_blank" rel="noopener noreferrer" className={styles.joinLink}> {baseUrl}</a> link in your browser and enter the event code above</li>
                            </ol>
                        </div>
                    </aside>
                )}
                <article className={styles.card}>
                    {(question || description || hasImage) && (
                        <div className={styles.cardTop}>
                            {question && (
                                <section className={styles.section}>
                                    <h1 className={styles.question}>{question}</h1>
                                </section>
                            )}
                            {description && (
                                <section className={styles.section}>
                                    <p className={styles.description}>{description}</p>
                                </section>
                            )}
                            {hasImage && (
                                <section className={styles.section}>
                                    <div className={styles.imageWrap}>
                                        <img src={imageUrl} alt="" className={styles.image} decoding="async" loading="lazy" />
                                    </div>
                                </section>
                            )}
                        </div>
                    )}

                    <div className={styles.resultsSection}>
                        <div className={styles.resultsHeader}>
                            <span className={styles.resultsLabel}>Results</span>
                            <span className={styles.resultsCount}>
                                {responses.length} response{responses.length !== 1 ? 's' : ''}
                            </span>
                        </div>
                        {responses.length > 0 ? (
                            <div className={styles.results}>
                                <ResultsView type={type} responses={responses} content={content} />
                            </div>
                        ) : (
                            <div className={styles.noResults}>No responses yet</div>
                        )}
                    </div>
                </article>
            </main>
        </div>
    );
}
